// Consolidated "Agregar usuario" modal — replaces the old separate "Invitar
// al equipo" and "Crear usuario" entry points with ONE flow that picks a
// método: send an invite (the person sets their own password) or create the
// account directly with a temporary password. Shared between Users.tsx and
// (via its onSuccess callback) anywhere else that needs to add a team member.
//
// invitación → POST /admin/users/invite via inviteTeamMember(); closes on
// success (no "done" screen — the person still has to click the emailed
// link). temporal → POST /admin/users via createUser(); on success the modal
// moves to a "done" phase showing either the one-time temp password (screen
// delivery) or a confirmation that it was emailed instead — moved verbatim
// from the old direct-create modal, not rewritten.

import { useState } from 'react'

import {
  Banner,
  Button,
  FormField,
  Input,
  Modal,
  RadioGroup,
  Select,
  toast,
} from '../../../design-system/index.js'
import { apiErrorMessage } from '../../../lib/api-error.js'
import { createUser, inviteTeamMember } from '../users-data.js'

type Method = 'invite' | 'temporal'
type DeliverCredentials = 'screen' | 'primary_email' | 'secondary_email'

export interface AddUserModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void | Promise<void>
  roles: readonly { value: string; label: string }[]
  /** Only a dueño may grant an owner/administrador role — controls the Rol
   *  field's helper text (same rail the server enforces). */
  isOwnerCaller: boolean
}

export function AddUserModal({
  open,
  onClose,
  onSuccess,
  roles,
  isOwnerCaller,
}: AddUserModalProps): JSX.Element {
  const [method, setMethod] = useState<Method>('invite')
  const [phase, setPhase] = useState<'form' | 'done'>('form')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<string>(roles[0]?.value ?? 'platform_account_manager')
  const [secondaryEmail, setSecondaryEmail] = useState('')
  const [deliveryEmail, setDeliveryEmail] = useState('')
  const [deliverCredentials, setDeliverCredentials] = useState<DeliverCredentials>('screen')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [tempPassword, setTempPassword] = useState('')
  const [deliveredTo, setDeliveredTo] = useState<'primary_email' | 'secondary_email' | null>(null)

  function close(): void {
    onClose()
    setMethod('invite')
    setPhase('form')
    setEmail('')
    setName('')
    setRole(roles[0]?.value ?? 'platform_account_manager')
    setSecondaryEmail('')
    setDeliveryEmail('')
    setDeliverCredentials('screen')
    setBusy(false)
    setError('')
    setTempPassword('')
    setDeliveredTo(null)
  }

  async function submitInvite(): Promise<void> {
    setBusy(true)
    setError('')
    try {
      const delivery = deliveryEmail.trim()
      const sent = delivery
        ? await inviteTeamMember(email.trim(), role, delivery)
        : await inviteTeamMember(email.trim(), role)
      if (sent.email_sent) {
        toast.ok('Invitación enviada', {
          description: `${delivery || email.trim()} ya puede entrar.`,
        })
      } else {
        toast.warn('Invitación creada, pero el correo no salió', {
          description: 'Revisa la sección de invitaciones pendientes y usa Reenviar.',
        })
      }
      close()
      await onSuccess()
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo enviar la invitación.'))
    } finally {
      setBusy(false)
    }
  }

  async function submitCreate(): Promise<void> {
    setBusy(true)
    setError('')
    try {
      const created = await createUser(email.trim(), name.trim(), role, {
        secondaryEmail: secondaryEmail.trim() || undefined,
        deliverCredentials,
      })
      if ('temp_password' in created) {
        setTempPassword(created.temp_password)
        setDeliveredTo(null)
      } else {
        setDeliveredTo(created.delivered_to)
      }
      setPhase('done')
      await onSuccess()
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo crear el usuario.'))
    } finally {
      setBusy(false)
    }
  }

  async function submit(): Promise<void> {
    if (method === 'invite') {
      await submitInvite()
    } else {
      await submitCreate()
    }
  }

  async function copyTempPassword(): Promise<void> {
    await navigator.clipboard.writeText(tempPassword)
    toast.ok('Copiada')
  }

  const emailValid = email.trim() !== ''
  const nameValid = method === 'invite' || name.trim() !== ''
  const deliveryValid = deliverCredentials !== 'secondary_email' || secondaryEmail.trim() !== ''
  const canSubmit = emailValid && nameValid && deliveryValid

  return (
    <Modal
      open={open}
      onClose={close}
      title={phase === 'form' ? 'Agregar usuario' : 'Usuario creado'}
      subtitle={
        phase === 'form'
          ? method === 'invite'
            ? 'Le llega un correo para crear su contraseña y entrar.'
            : 'Crea la cuenta directamente, sin correo de invitación.'
          : `La cuenta de ${email.trim()} ya existe.`
      }
      busy={busy}
      footer={
        phase === 'form' ? (
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void submit()} loading={busy} disabled={!canSubmit}>
              {method === 'invite' ? 'Enviar invitación' : 'Crear usuario'}
            </Button>
          </>
        ) : (
          <Button onClick={close}>Cerrar</Button>
        )
      }
    >
      {phase === 'form' ? (
        <div className="flex flex-col gap-3.5">
          {error !== '' && <Banner tone="warn">{error}</Banner>}
          <FormField label="Correo" htmlFor="au-email">
            <Input
              id="au-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
            />
          </FormField>
          {method === 'temporal' && (
            <FormField label="Nombre" htmlFor="au-name">
              <Input id="au-name" value={name} onChange={(e) => setName(e.target.value)} />
            </FormField>
          )}
          <FormField
            label="Rol"
            htmlFor="au-role"
            helper={
              isOwnerCaller
                ? 'Define qué puede ver y hacer dentro de INHERTA.'
                : 'Solo un dueño puede nombrar administradores o dueños.'
            }
          >
            <Select
              id="au-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              options={roles.map((r) => ({ value: r.value, label: r.label }))}
            />
          </FormField>
          <FormField label="Correo secundario (opcional)" htmlFor="au-secondary-email">
            <Input
              id="au-secondary-email"
              type="email"
              value={secondaryEmail}
              onChange={(e) => {
                setSecondaryEmail(e.target.value)
                if (e.target.value.trim() === '' && deliverCredentials === 'secondary_email') {
                  setDeliverCredentials('screen')
                }
              }}
              placeholder="otro-correo@ejemplo.com"
            />
          </FormField>

          <RadioGroup
            label="Método"
            name="au-method"
            value={method}
            onChange={setMethod}
            options={[
              {
                value: 'invite',
                label: 'Enviar invitación',
                description: 'La persona configura su propia contraseña desde el enlace.',
              },
              {
                value: 'temporal',
                label: 'Crear con contraseña temporal',
                description: 'La cuenta existe de inmediato; deberá cambiarla al entrar.',
              },
            ]}
          />

          {method === 'invite' ? (
            <FormField
              label="Enviar la invitación a otro correo (opcional)"
              htmlFor="au-delivery-email"
              helper="Si lo dejas vacío, la invitación llega al correo de la cuenta."
            >
              <Input
                id="au-delivery-email"
                type="email"
                value={deliveryEmail}
                onChange={(e) => setDeliveryEmail(e.target.value)}
                placeholder="otro-correo@ejemplo.com"
              />
            </FormField>
          ) : (
            <RadioGroup
              label="Entregar credenciales"
              name="au-deliver"
              value={deliverCredentials}
              onChange={setDeliverCredentials}
              options={[
                { value: 'screen', label: 'Mostrar contraseña en pantalla' },
                { value: 'primary_email', label: 'Enviar al correo del usuario' },
                {
                  value: 'secondary_email',
                  label: 'Enviar al correo secundario',
                  disabled: secondaryEmail.trim() === '',
                  ...(secondaryEmail.trim() === ''
                    ? { description: 'Agrega un correo secundario para habilitarlo.' }
                    : {}),
                },
              ]}
            />
          )}
        </div>
      ) : deliveredTo ? (
        <p className="text-[12.5px] text-text-2">
          {deliveredTo === 'primary_email'
            ? 'Enviamos las credenciales al correo del usuario.'
            : 'Enviamos las credenciales al correo secundario.'}
        </p>
      ) : (
        <div className="flex flex-col gap-3.5">
          <p className="text-[12.5px] text-text-2">
            La cuenta de <b className="font-semibold text-text">{email.trim()}</b> quedó creada.
            Esta es su <b className="font-semibold text-text">contraseña temporal</b>: compártela tú
            directamente (no se envía ningún correo). La persona la usará para entrar la primera vez
            y el sistema le pedirá crear la suya.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-md border border-line bg-surface-2 px-3 py-2 font-mono text-[12.5px] text-text">
              {tempPassword}
            </code>
            <Button variant="secondary" onClick={() => void copyTempPassword()}>
              Copiar
            </Button>
          </div>
          <Banner tone="warn">Guárdala ahora: no se volverá a mostrar.</Banner>
        </div>
      )}
    </Modal>
  )
}
